// @file Composable to encapsulate billing maintenance mode logic.
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

function getDateStringForDisplay(date: Date): string {
  return date.toLocaleString(undefined, {
    hour: 'numeric',
    minute: 'numeric',
    year: 'numeric',
    day: 'numeric',
    month: 'long',
  })
}

export const useBillingMaintenanceModeStore = defineStore('billingMaintenanceModeStore', () => {
  const isBillingMaintenanceMode = ref<boolean>(window.ww?.vueStartingState?.billingMaintenanceMode?.enabled || false)
  const billingMaintenanceModeStartDate = ref<string | null>(
    window.ww?.vueStartingState?.billingMaintenanceMode?.startDate || null,
  )

  const billingMaintenanceModeEndDate = computed<string | null>(() => {
    const startDateInSeconds = billingMaintenanceModeStartDate.value
    if (startDateInSeconds == null) return null
    return getDateStringForDisplay(new Date((Number(startDateInSeconds) + 24 * 60 * 60) * 1000))
  })

  const billingMaintenanceModeText = computed<string>(() =>
    billingMaintenanceModeEndDate.value == null
      ? __(
          'Our billing system is undergoing maintenance and will be available soon. Reach out to us at <a href="mailto:%{supportEmail}" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">%{supportEmail}</a> if you need any assistance in the meantime.',
          {
            supportEmail: 'hello@padlet.com',
          },
        )
      : __(
          'Our billing system is undergoing maintenance. It will be available by %{maintenanceEndDate}. Reach out to us at <a href="mailto:%{supportEmail}" class="oz-text-link-light-primary dark:oz-text-link-dark-primary">%{supportEmail}</a> if you need any assistance in the meantime.',
          {
            supportEmail: 'hello@padlet.com',
            maintenanceEndDate: billingMaintenanceModeEndDate.value,
          },
        ),
  )

  const billingMaintenanceModeShortText = computed<string>(() =>
    billingMaintenanceModeEndDate.value == null
      ? __('Our billing system is undergoing maintenance and will be available soon.')
      : __('Our billing system is undergoing maintenance. It will be available by %{maintenanceEndDate}.', {
          maintenanceEndDate: billingMaintenanceModeEndDate.value,
        }),
  )

  const setBillingMaintenanceMode = (
    billingMaintenanceMode: { enabled: boolean; startDate: string | number | null } | undefined,
  ): void => {
    isBillingMaintenanceMode.value = billingMaintenanceMode?.enabled ?? false
    billingMaintenanceModeStartDate.value = billingMaintenanceMode?.startDate
      ? billingMaintenanceMode.startDate.toString()
      : null
  }

  return {
    // Getters
    isBillingMaintenanceMode,
    billingMaintenanceModeText,
    billingMaintenanceModeShortText,

    // Actions
    setBillingMaintenanceMode,
  }
})
